import { useContentMessages } from '@/features/editor/hooks/useContentMessages'
import { useMessages } from '@/i18n'
import { messages } from './messages'
import { useState, type CSSProperties, type ReactNode } from 'react'
import { ImagePlus, X } from 'lucide-react'
import { blockTheme, cn, hsl, Popover, PopoverContent, PopoverTrigger } from '@teach/ui'
import type { DesignBadgeItem } from '@/types/blocks'
import type { ContentLang } from '@/features/editor/editorSlice'
import { DESIGN_LIMITS } from '@/features/editor/lib/designElements'
import { getLocalizedField, setLocalizedField } from '@/features/editor/lib/localizedField'
import { badgeSizeClass, type AutoBadgeSize } from '@/features/editor/lib/badgeStyles'
import { IconGrid } from '../../design/IconGrid'
import { setDesignToolbarSuppressed } from '@/features/editor/lib/designToolbarSuppressed'

interface Props {
  badge: DesignBadgeItem
  size: AutoBadgeSize
  style: CSSProperties
  contentLang: ContentLang
  /** Иконка бейджа уже отрисованная (`BadgeIcon`) или `null`. */
  icon: ReactNode
  onPatch: (patch: Partial<DesignBadgeItem>) => void
  /** Удалить бейдж; нет — последний бейдж в списке (пустым элемент не бывает). */
  onRemove?: () => void
}

// Кнопки на наведении на ЭТОТ бейдж — кружки поверх краёв пилюли, вне потока:
// ширина пилюли не меняется, ряд не переносится, ничего не дёргается под мышью.
const edgeStyle: CSSProperties = {
  backgroundColor: hsl(blockTheme.background, 0.85),
  borderColor: hsl(blockTheme.background),
}
const edgeBtn =
  'absolute top-1/2 z-10 flex size-[1.4em] -translate-y-1/2 items-center justify-center rounded-full border p-[0.25em] opacity-0 shadow-md backdrop-blur transition group-hover/pill:opacity-100 hover:scale-110'

/**
 * Бейдж прямо на холсте: текст правится на месте, а при наведении на пилюлю по её
 * краям появляются кружки — слева значок картинки — выбрать иконку (та же сетка `IconGrid`, что в
 * панели), справа крестик удаления. Есть иконка — клик по ней открывает выбор.
 */
export const EditableBadgePill = ({
  badge,
  size,
  style,
  contentLang,
  icon,
  onPatch,
  onRemove,
}: Props) => {
  const m = useMessages(messages)
  // Подсказки полей — на языке правки: видно, что правится английская версия.
  const cm = useContentMessages(messages)
  const [open, setOpenState] = useState(false)
  // Пока открыт выбор иконки — тёмная панель настроек элемента прячется под ним.
  const setOpen = (next: boolean) => {
    setOpenState(next)
    setDesignToolbarSuppressed(next)
  }
  const text = getLocalizedField(badge, 'text', contentLang) ?? ''
  return (
    <span
      className={cn(
        'group/pill relative inline-flex items-center rounded-full font-semibold',
        badgeSizeClass(size),
      )}
      style={style}
    >
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <button
            type="button"
            aria-label={m.badgeIcon}
            onClick={(e) => e.stopPropagation()}
            className={cn(
              icon
                ? 'flex shrink-0 items-center justify-center rounded-full transition hover:scale-110'
                : cn(edgeBtn, '-left-[0.5em]'),
            )}
            style={icon ? undefined : edgeStyle}
          >
            {icon ?? <ImagePlus className="size-full" />}
          </button>
        </PopoverTrigger>
        <PopoverContent className="w-60 p-2" align="center" onClick={(e) => e.stopPropagation()}>
          {/* Сразу сетка с поиском, без вкладок; первая клетка — «без иконки». */}
          <IconGrid
            value={badge.iconType === 'lucide' ? badge.iconValue : null}
            onPick={(iconValue) => {
              onPatch({ iconType: 'lucide', iconValue })
              setOpen(false)
            }}
            onClear={() => {
              onPatch({ iconType: 'none', iconValue: null })
              setOpen(false)
            }}
          />
        </PopoverContent>
      </Popover>
      <input
        value={text}
        aria-label={m.badgeText}
        maxLength={DESIGN_LIMITS.BADGE_TEXT}
        placeholder={cm.badge}
        size={Math.max(text.length, 4)}
        onChange={(e) =>
          onPatch(
            setLocalizedField(
              'text',
              contentLang,
              e.target.value.slice(0, DESIGN_LIMITS.BADGE_TEXT),
            ),
          )
        }
        // Ширина по тексту: поле не растягивает пилюлю шире её содержимого.
        style={{ fieldSizing: 'content' } as CSSProperties}
        className="min-w-[2ch] bg-transparent text-center font-semibold text-inherit outline-none placeholder:text-current placeholder:opacity-50"
      />
      {onRemove ? (
        <button
          type="button"
          aria-label={m.removeBadge}
          onClick={(e) => {
            e.stopPropagation()
            onRemove()
          }}
          className={cn(edgeBtn, '-right-[0.5em]')}
          style={edgeStyle}
        >
          <X className="size-full" />
        </button>
      ) : null}
    </span>
  )
}
